import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { MessagePart } from 'tripPlanner/components/Chat/Message'
import { UI_COMPONENT_TYPES } from 'tripPlanner/components/Chat/constants'
import { stripMarkdown } from 'lib/string/stripMarkdown'

interface UseClipboardArgs {
  lastAssistantMessage?: MessageItem
  isDevMode: boolean
}

interface ClipboardLabels {
  options: string
  hotelCarousel: string
  experienceCarousel: string
  cruiseCarousel: string
  cart: string
}

function serializeUIComponentForClipboard(
  part: Extract<MessagePart, { type: 'ui_component' }>,
  labels: ClipboardLabels,
): string {
  const data = part.data

  if (!data) {
    return ''
  }

  if (data.type === 'pills_list') {
    const optionsText = data.options.map(opt => `- ${opt.label}`).join('\n')
    return `${labels.options}:\n${optionsText}`.trim()
  }

  if (data.type === 'cards_list') {
    const optionsText = data.options
      .map(opt => `- ${(opt.emoji ? `${opt.emoji} ` : '') + opt.title}${opt.description ? `: ${opt.description}` : ''}`)
      .join('\n')
    return `${labels.options}:\n${optionsText}`.trim()
  }

  if (data.type === UI_COMPONENT_TYPES.CAROUSEL) {
    return data.title ??
      (data.product === 'cruise' ?
        labels.cruiseCarousel :
        data.product === 'experience' ?
          labels.experienceCarousel :
          labels.hotelCarousel)
  }

  // location_entities are map-only data, not useful in clipboard
  if (data.type === 'location_entities') {
    return ''
  }

  // The cart widget holds no message data — its contents are fetched live when it
  // renders — so copy a label rather than a snapshot that would already be stale.
  if (data.type === UI_COMPONENT_TYPES.CART) {
    return labels.cart
  }

  // Inline map cards copy as pin names
  if (data.type === UI_COMPONENT_TYPES.MAP) {
    return data.pins.map(pin => pin.name).filter(Boolean).join(', ')
  }

  try {
    return JSON.stringify(data)
  } catch {
    return ''
  }
}

function partsToClipboardText(
  parts: Array<MessagePart>,
  labels: ClipboardLabels,
): string {
  return parts
    .map((p) => {
      if (p.type === 'text') {
        return stripMarkdown(p.content || '')
      }
      if (p.type === 'trip_plan_content') {
        return stripMarkdown(p.content || '')
      }
      if (p.type === 'ui_component') {
        return serializeUIComponentForClipboard(p, labels)
      }
      return ''
    })
    .filter(Boolean)
    .join('\n\n')
    .trim()
}

export function useClipboard({ lastAssistantMessage, isDevMode }: UseClipboardArgs) {
  const intl = useIntl()
  const [copiedRecently, setCopiedRecently] = useState(false)
  const copyTimeoutRef = useRef<number | undefined>(undefined)
  const optionsLabel = intl.formatMessage({
    id: 'EV-n0C4MNPFNHABFugxT',
    defaultMessage: 'Options',
    description: 'Heading label used when copying selectable chat options to the clipboard in trip planner',
  })
  const defaultHotelCarouselLabel = intl.formatMessage({
    id: 'l06aaJjBUwR-eQ-5874E',
    defaultMessage: 'Top Hotel Picks',
    description: 'Default heading for the hotel carousel when no search query is provided',
  })
  const defaultExperienceCarouselLabel = intl.formatMessage({
    id: 'vJhmJAn2elKiRffXAdo5',
    defaultMessage: 'Top Experience Picks',
    description: 'Default heading for the experience carousel when no search query is provided',
  })
  const defaultCruiseCarouselLabel = intl.formatMessage({
    id: 's_QqH--qGB4d3h-T7nIa',
    defaultMessage: 'Top Cruise Picks',
    description: 'Default heading for the cruise carousel when no search query is provided',
  })

  const cartLabel = intl.formatMessage({
    id: 'KTfuxaACrTyQp8QTN40V',
    defaultMessage: 'Your cart',
    description: 'Heading of the cart widget shown inline in trip planner chat above the bookable trip items',
  })

  const handleCopyResponse = useCallback(() => {
    if (!lastAssistantMessage) {
      return
    }
    const partsText = partsToClipboardText(lastAssistantMessage.parts, {
      options: optionsLabel,
      hotelCarousel: defaultHotelCarouselLabel,
      experienceCarousel: defaultExperienceCarouselLabel,
      cruiseCarousel: defaultCruiseCarouselLabel,
      cart: cartLabel,
    })
    const rawText = isDevMode && lastAssistantMessage.rawContent ? lastAssistantMessage.rawContent : ''
    const textToCopy = rawText
      ? [rawText.trim(), partsText].filter(Boolean).join('\n\n')
      : partsText
    navigator.clipboard?.writeText(textToCopy)
      .then(() => {
        setCopiedRecently(true)
        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = window.setTimeout(() => {
          setCopiedRecently(false)
          copyTimeoutRef.current = undefined
        }, 1000)
      })
      .catch(() => { })
  }, [cartLabel, defaultCruiseCarouselLabel, defaultExperienceCarouselLabel, defaultHotelCarouselLabel, isDevMode, lastAssistantMessage, optionsLabel])

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  return { copiedRecently, handleCopyResponse }
}
